/*
 *  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { EditorRoutingManager } from "../../../runtime/EditorRoutingManager";
import {
	createEventEmitter,
	emitEvent,
	isNil,
	offEvent,
	onEvent,
	removeAllEventListeners
} from "@adapptio/lib-shared";

type THistoryItem = string;

export interface IAppPreviewHistoryState {
	loading: boolean;
	previous: THistoryItem | null;
	current: THistoryItem | null;
	next: THistoryItem | null;
}

export class AppPreviewHistory {
	private routingManager: EditorRoutingManager;

	/* History items */
	private currentIndex = 0;
	private items: THistoryItem[] = [];
	private queue: THistoryItem[] = [];

	/* Events */
	public onChange = createEventEmitter<void>();

	constructor({ routingManager }: { routingManager: EditorRoutingManager }) {
		this.routingManager = routingManager;
		this.items.push(routingManager.getCurrentLocationAsString() || "/");

		onEvent(this.routingManager.onNavigate, this.handleNavigationChange);
	}

	public dispose = (): void => {
		offEvent(this.routingManager.onNavigate, this.handleNavigationChange);

		removeAllEventListeners(this.onChange);
	};

	private emitChange = (): void => {
		emitEvent(this.onChange);
	};

	private handleNavigationChange = (): void => {
		const newPath = this.routingManager.getCurrentLocationAsString();

		// History item
		if (newPath === this.queue[0]) {
			this.queue.shift();
			this.emitChange();
			return;
		}

		// History item with race condition
		if (newPath === this.queue[this.queue.length - 1]) {
			this.queue = [];
			this.emitChange();
			return;
		}

		// Refresh
		if (newPath === this.current) {
			return;
		}

		// New item
		this.items.length = this.currentIndex + 1;
		this.items.push(newPath);
		this.currentIndex = this.items.length - 1;
		this.emitChange();
	};

	private navigateTo = (path: THistoryItem): void => {
		this.queue.push(path);
		this.routingManager.navigate(path);
	};

	public go = (delta: number): void => {
		let newIndex = this.currentIndex + delta;

		if (isNil(this.items[newIndex])) {
			if (delta < 0) {
				newIndex = 0;
			}

			if (delta > 0) {
				newIndex = this.items.length - 1;
			}
		}

		this.currentIndex = newIndex;
		this.emitChange();
		this.navigateTo(this.items[this.currentIndex]);
	};

	public get previous(): THistoryItem | undefined {
		return this.items[this.currentIndex - 1];
	}

	public get current(): THistoryItem | undefined {
		return this.items[this.currentIndex];
	}

	public get next(): THistoryItem | undefined {
		return this.items[this.currentIndex + 1];
	}

	public goBack = (): void => {
		if (isNil(this.previous)) {
			return;
		}

		this.go(-1);
	};

	public goForward = (): void => {
		if (isNil(this.next)) {
			return;
		}

		this.go(1);
	};

	public getState = (): IAppPreviewHistoryState => ({
		loading: this.queue.length > 0,
		previous: this.previous,
		current: this.current,
		next: this.next
	});
}
